/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Guest Order Lookup ("Track Order") — the headless replacement for SFRA's Order-Track →
 * Order-GuestDetails → Order-Details journey, collapsed into one read-only page.
 *
 * A guest POSTs order number + email + billing postal code to this route's `action`, which
 * calls `lookupGuestOrder`. On success the page swaps the lookup form for the order view on
 * the same URL (a navigating `<Form>`, the SFNext analogue of SFRA's redirect). The order
 * token returned by the lookup is carried in `useActionData` (React state) only — it is
 * never persisted to a cookie, localStorage, or the URL, and never logged. Later milestones
 * (cancel / return) read it from `actionData` to authorize mutations.
 *
 * Turnstile bot protection: the `TrackOrderLookupForm` renders the Turnstile widget when
 * enabled, and this route's `action` verifies its token at the BFF (`enforceTurnstile`) before
 * `lookupGuestOrder` runs. When Turnstile is disabled the check is a no-op, so the success/error
 * contract below is unchanged either way.
 */
import type { ReactElement } from 'react';
import { useActionData } from 'react-router';
import type { Route } from './+types/_app.track-order';
import { useTranslation } from 'react-i18next';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { SeoMeta } from '@/components/seo-meta';
import { Card } from '@/components/ui/card';
import { Typography } from '@/components/typography';
import { TrackOrderLookupForm, TrackOrderResult } from '@/components/track-order';
import { lookupGuestOrder, type GuestOrderMeta, type SomOrderModel } from '@/lib/order/guest-lookup.server';
import { enforceTurnstile } from '@/lib/turnstile/enforce.server';
import { getLogger } from '@/lib/logger.server';

type TrackOrderActionData =
    | { error: string; order?: undefined; orderToken?: undefined; meta?: undefined }
    // `orderToken` is held in React state for the cancel/return flows; the read-only view
    // renders entirely from `order`. `meta` carries the cancel/return reason lists so the
    // mutation dialogs can render their dropdowns with no extra round trip.
    | { error?: undefined; order: SomOrderModel; orderToken: string; meta: GuestOrderMeta };

/**
 * Verifies the guest's identity against the order and returns the SOM model. All identity
 * failures collapse to a single `notFound` message so the endpoint cannot be used to probe
 * which field was wrong; a service/CDN failure returns `unavailable`. Submitted values are
 * never logged (see `lookupGuestOrder`).
 */
export async function action({ request, context }: Route.ActionArgs): Promise<TrackOrderActionData> {
    const { t } = getTranslation(context);
    const formData = await request.formData();
    const orderNo = formData.get('orderNo')?.toString().trim();
    const email = formData.get('email')?.toString().trim();
    const postalCode = formData.get('postalCode')?.toString().trim();

    if (!orderNo || !email || !postalCode) {
        return { error: t('trackOrder:error.missingFields') };
    }

    // Bot protection before the lookup runs: this public endpoint returns full name, address,
    // masked card, and the order token for a matching order#/email/ZIP, so throttling brute-force
    // enumeration matters. `enforceTurnstile` is a no-op when Turnstile is disabled. The shopper's
    // email is intentionally NOT passed (and so never reaches Turnstile logs) to keep the route's
    // "submitted values are never logged" guarantee.
    const turnstileToken = formData.get('turnstileToken')?.toString();
    const allowed = await enforceTurnstile({
        request,
        config: getConfig(context),
        turnstileToken,
        logger: getLogger(context),
        actionName: 'guest-order-lookup',
    });
    if (!allowed) {
        return { error: t('trackOrder:error.unavailable') };
    }

    const result = await lookupGuestOrder(context, { orderNo, email, postalCode });
    if (!result.ok) {
        return {
            error: t(result.reason === 'not-found' ? 'trackOrder:error.notFound' : 'trackOrder:error.unavailable'),
        };
    }

    return { order: result.order, orderToken: result.orderToken, meta: result.meta };
}

/** Guest Track Order page: lookup form, or the read-only order view after a successful lookup. */
export default function TrackOrder(): ReactElement {
    const actionData = useActionData<typeof action>();
    const { t } = useTranslation('trackOrder');

    if (actionData?.order) {
        return (
            <div className="section-container py-8">
                <SeoMeta title={t('metaTitle')} noIndex />
                <div className="mx-auto max-w-3xl">
                    <TrackOrderResult
                        order={actionData.order}
                        orderToken={actionData.orderToken}
                        meta={actionData.meta}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="section-container py-12">
            <SeoMeta title={t('metaTitle')} noIndex />
            <div className="mx-auto w-full max-w-md space-y-8">
                <div className="text-center">
                    <Typography variant="h3" as="h1">
                        {t('title')}
                    </Typography>
                    <p className="mt-2 text-sm text-muted-foreground">{t('description')}</p>
                </div>

                <Card className="p-8">
                    <TrackOrderLookupForm error={actionData?.error} />
                </Card>
            </div>
        </div>
    );
}
